(ns bp-clients.tv-app.utils.logger
  "This namespace contains all functions to log to rollbar,
  including rollbar instance.

  This instance should be accessed only by worker thread.
  That is why is made a dispatch when functions that invoke
  rollbar are called from main thread."
  (:require
   ["mux-embed" :as mux]
   ["rollbar" :as Rollbar]
   [bp-clients.tv-app.utils.os :as os-system]
   [goog.object]
   [re-frame.core :as rf :refer [reg-event-fx path]])
  (:require-macros
   [bp-libs.aero.js :refer [get-config]]))

(def logger-path ::logger)

(def ^:private logger-interceptors
  "Checks spec and selects logger path interceptor"
  [(path logger-path)])

(def ^:private levels
  "Least to most verbose"
  {:debug 5 :info 4 :warn 3 :error 2 :critical 1 :disabled 0})

(goog-define logger-level "debug") ;; this must be a string
(goog-define rollbar? false) ;; this should be only enabled for production
(goog-define code-version "dev")
(goog-define env "dev")

(def mux-config (get-config :mux))

(defn mux-setup
  "Setups video player metrics using mux
   https://docs.mux.com/guides/monitor-html5-video-element"
  [{:keys [video-el video-id video-title video-duration video-encoding-variant video-producer viewer-user-id view-drm-type playlist-id]}]
  (let [init-time (-> (.-default mux) .-utils .now)]
    (.monitor (.-default mux)
              video-el
              #js{"debug" false
                  "data" #js{"env_key" (:envkey mux-config)
                             "video_id" video-id
                             "video_title" video-title
                             "video_duration" video-duration
                             "video_enconding_variant" video-encoding-variant
                             "video_producer" video-producer
                             "viewer_user_id" viewer-user-id
                             "view_drm_type" view-drm-type
                             "player_init_time" init-time
                             "page_type" "player"
                             "player_name" "tv"
                             "custom_1" playlist-id}})))

(def ^:private rollbar-exceptions-ignore
  #{"Failed to execute 'sendBeacon' on 'Navigator': sendBeacon() with a Blob whose type is not any of the CORS-safelisted values for the Content-Type request header is disabled temporarily. See http://crbug.com/490015 for details."
    "ResizeObserver loop limit exceeded"})

(def ^:private rollbar-payload-config
  {:environment env
   :enabled     rollbar?
   :payload     {:device os-system/user-agent
                 :client {:javascript {:source_map_enabled true
                                       :guess_uncaught_frames true
                                       :code_version code-version}}}
   :checkIgnore (fn [_is-uncaught _args payload]
                  (->> payload
                       .-body
                       .-trace
                       .-exception
                       .-description
                       (contains? rollbar-exceptions-ignore)))})

(defn create-rollbar []
  (new Rollbar (clj->js (merge (get-config :rollbar) rollbar-payload-config))))

(def rollbar (create-rollbar))

(defn- level-at-least
  [level-k current-str]
  (let [level   (or (level-k levels) js/NaN)
        current (or (-> current-str keyword levels) js/NaN)]
    (>= current level)))

(defn- logger
  [js-fn rollbar-fn level]
  (try (when (level-at-least level logger-level)
         (js-fn)
         (when rollbar?
           (rollbar-fn)))
       (catch js/Error e
         (js/console.log "error to send log to rollbar" e))))

(defn log
  [title & args]
  (logger #(apply js/console.log title args)
          #(.log rollbar (apply str title args))
          :debug)
  nil)

(defn- debug-ev
  [db [_event-name message & args]]
  (apply log message args)
  {:db db})

(defn info
  [title & args]
  (logger #(apply js/console.log title args)
          #(.info rollbar (apply str title args))
          :info)
  nil)

(defn- info-ev
  [db [_event-name title & args]]
  (apply info title args)
  {:db db})

(defn warn
  [title & args]
  (logger #(apply js/console.log title args)
          #(.warn rollbar (apply str title args))
          :warn)
  nil)

(defn- warn-ev
  [db [_event-name title & args]]
  (apply warn title args)
  {:db db})

(defn- severity-str [error]
  (str "severity: " (case (.-severity error)
                      1 "RECOVERABLE"
                      2 "CRITICAL"
                      nil)))

(defn js-error
  "The argument must be a js/Error or an error event."
  [js-error]
  (logger #(js/console.error (.-message js-error))
          #(.error rollbar (.-message js-error) (.-arguments js-error) (severity-str js-error) js-error)
          :error)
  nil)

(defn- js-error-ev
  [db [_event-name error]]
  (let [js-error-instance (js/Error. (:message error))]
    (when (and js-error-instance (:stack error))
      (goog.object/set js-error-instance "stack" (:stack error)))
    (when (and js-error-instance (:errorData error))
      (goog.object/set js-error-instance "arguments" (:errorData error)))
    (when (and js-error-instance (:severity error))
      (goog.object/set js-error-instance "severity" (:severity error)))
    (js-error js-error-instance))
  {:db db})

(defn promise-error
  [e]
  (logger #(js/console.error (or (.-message e) (-> e .-reason .-message)))
          #(.error rollbar (or (.-message e) (-> e .-reason .-message)) e)
          :error)
  nil)

(defn- promise-error-ev
  [db [_event-name promise-error]]
  (let [e (js/Error. (:message promise-error))]
    (goog.object/set e "stack" (:stack promise-error))
    (promise-error e))
  {:db db})

(defn error
  "Log an error with only string arguments."
  [title & args]
  (logger #(apply js/console.error title args)
          #(.error rollbar (apply str title args))
          :error)
  nil)

(defn- error-ev
  [db [_event-name title & args]]
  (apply error title args)
  {:db db})

(defn critical
  [title & args]
  (logger #(apply js/console.error title args)
          #(.critical rollbar (apply str title args))
          :critical)
  nil)

(defn- critical-ev
  [db [_event-name title & args]]
  (apply critical title args)
  {:db db})

(defn set-person!
  "Allows tracking errors to a specific user after being logged in.
   Call this to set or unset the person to be tracked.
  :id is required, if not provided email is used instead. "
  [{:keys [id email] :as person}]
  (.configure rollbar (clj->js {:payload {:person (merge person {:id (or id email)})}}))
  nil)

(defn- set-person-ev
  [db [_ person]]
  (set-person! person)
  db)

(defn set-context!
  "Allows context setting for tracking errors.
  Currently used for routes."
  [context]
  (.configure rollbar (clj->js {:payload {:context context}}))
  nil)

(defn set-session-id!
  [session-id]
  (.configure rollbar (clj->js {:payload {:session-id session-id}}))
  nil)

(defn- set-context-ev
  [db [_ context]]
  (set-context! context)
  db)

(defn set-player-info!
  "Set player info to rollbar."
  [player-data]
  (.configure rollbar (clj->js {:player player-data}))
  nil)

(defn- set-player-info-ev
  [db [_ data]]
  (set-player-info! data)
  db)

(defn set-device-info!
  [device-info]
  (.configure rollbar (clj->js {:payload {:device-info device-info}}))
  nil)

(defn- set-device-info-ev
  [db [_ device-info]]
  (set-device-info! device-info)
  db)

(reg-event-fx ::error logger-interceptors error-ev)
(reg-event-fx ::js-error logger-interceptors js-error-ev)
(reg-event-fx ::promise-error logger-interceptors promise-error-ev)
(reg-event-fx ::info logger-interceptors info-ev)
(reg-event-fx ::warn logger-interceptors warn-ev)
(reg-event-fx ::critical logger-interceptors critical-ev)
(reg-event-fx ::debug logger-interceptors debug-ev)
(reg-event-fx ::set-person! logger-interceptors set-person-ev)
(reg-event-fx ::set-context! logger-interceptors set-context-ev)
(reg-event-fx ::set-player-info! logger-interceptors set-player-info-ev)
(reg-event-fx ::set-device-info! logger-interceptors set-device-info-ev)

(goog-define is-test? false)

(when ^boolean is-test?
  ;; when is-test? is on we remove warnings so tests logs are cleaner
  ;; this is specilly true because we replace/rewrite events before/after test runs with mocks
  ;; error logs remain the same
  (rf/set-loggers! {:warn identity}))

;; https://day8.github.io/re-frame/api-re-frame.core/#set-loggers
(rf/set-loggers! {:error error})
